import { useState } from "react"
import { createPortal } from "react-dom"

import { type VacProps, weekOfDate } from "@/lib/roadmapDom"

export interface VacState {
  el: Element
  props: VacProps
  isNew: boolean
}

/** Editor for a Key-vacation chip: name + start/end date (+ optional note). The
 *  chip is auto-positioned to the roadmap week(s) the dates fall in. */
export function VacationModal({
  state,
  onApply,
  onCancel,
  onDelete,
}: {
  state: VacState
  onApply: (p: VacProps) => void
  onCancel: () => void
  onDelete: () => void
}) {
  const { props, isNew } = state
  const [name, setName] = useState(props.name)
  const [start, setStart] = useState(props.start)
  const [end, setEnd] = useState(props.end)
  const [note, setNote] = useState(props.note)

  const s = weekOfDate(start)
  const e = Math.max(s, weekOfDate(end || start))
  const placement = e > s ? `Weeks W${s}–W${e}` : `Week W${s}`

  const field = "rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700"

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="w-full max-w-sm space-y-3 rounded-xl bg-white p-4 shadow-xl" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">{isNew ? "New vacation" : "Edit vacation"}</h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600" aria-label="Close">
            ✕
          </button>
        </div>

        <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Name
          <input value={name} onChange={(ev) => setName(ev.target.value)} className={`mt-1 w-full font-normal normal-case tracking-normal ${field}`} />
        </label>

        <div className="flex gap-3">
          <label className="flex flex-1 flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Start date
            <input type="date" value={start} onChange={(ev) => setStart(ev.target.value)} className={`font-normal normal-case tracking-normal ${field}`} />
          </label>
          <label className="flex flex-1 flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            End date
            <input type="date" value={end} onChange={(ev) => setEnd(ev.target.value)} className={`font-normal normal-case tracking-normal ${field}`} />
          </label>
        </div>

        <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Note (optional)
          <input value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="e.g. 19.5 working days" className={`mt-1 w-full font-normal normal-case tracking-normal ${field}`} />
        </label>

        <p className="text-[11px] text-slate-400">
          Auto-placed → <b className="text-slate-600">{placement}</b>
        </p>

        <div className="flex items-center justify-between pt-1">
          <button onClick={onDelete} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50">
            Delete
          </button>
          <div className="flex gap-2">
            <button onClick={onCancel} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50">
              Cancel
            </button>
            <button
              onClick={() => onApply({ name, start, end: end || start, note })}
              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
